import { operaApiHeaders, authFetch } from "@/modules/auth/api/authApi";
import { apiUrl } from "@/lib/runtimeUrls";

const CMS_API_BASE = "/api/cms/index.php";

type ApiSuccess<T> = { success: true; data: T };
type ApiFailure = { success: false; message: string; errors?: string[] };

export class CmsApiError extends Error {
  status: number;
  errors: string[];
  body: string;
  url: string;

  constructor(message: string, status = 500, errors: string[] = [], body = "", url = "") {
    super(message);
    this.name = "CmsApiError";
    this.status = status;
    this.errors = errors;
    this.body = body;
    this.url = url;
  }
}

function authHeaders(): HeadersInit {
  return operaApiHeaders(false);
}

function buildUrl(route: string, query?: Record<string, string>) {
  const resolved = apiUrl(CMS_API_BASE);
  const url = new URL(resolved, typeof window !== "undefined" ? window.location.origin : "http://localhost");
  url.searchParams.set("route", route);
  if (query) {
    Object.entries(query).forEach(([key, value]) => url.searchParams.set(key, value));
  }
  return url.toString();
}

function logApiFailure(url: string, status: number, body: string) {
  console.error("[CMS API] Request failed", { url, status, body });
}

async function parseResponse<T>(res: Response, url: string): Promise<T> {
  const contentType = res.headers.get("content-type") ?? "";
  const raw = await res.text();

  if (!contentType.includes("application/json")) {
    logApiFailure(url, res.status, raw);
    throw new CmsApiError(
      `API returned non-JSON (${contentType || "unknown"}). Check PHP errors for this endpoint.`,
      res.status,
      [],
      raw,
      url
    );
  }

  let parsed: ApiSuccess<T> | ApiFailure;
  try {
    parsed = JSON.parse(raw) as ApiSuccess<T> | ApiFailure;
  } catch {
    logApiFailure(url, res.status, raw);
    throw new CmsApiError("API returned invalid JSON", res.status, [], raw, url);
  }

  if (!res.ok || parsed.success === false) {
    const failure = parsed as ApiFailure;
    const message = failure.message ?? `Request failed (${res.status})`;
    logApiFailure(url, res.status, raw);
    throw new CmsApiError(message, res.status, failure.errors ?? [], raw, url);
  }

  return (parsed as ApiSuccess<T>).data;
}

export async function cmsRequest<T>(
  route: string,
  options: RequestInit & { query?: Record<string, string> } = {}
): Promise<T> {
  const { query, headers, ...rest } = options;
  const url = buildUrl(route, query);
  const res = await authFetch(url, {
    ...rest,
    headers: {
      ...authHeaders(),
      ...(rest.body && !(rest.body instanceof FormData) ? { "Content-Type": "application/json" } : {}),
      ...headers,
    },
  });
  return parseResponse<T>(res, url);
}

export async function cmsUploadFile(file: File): Promise<string> {
  const form = new FormData();
  form.append("file", file);
  const data = await cmsRequest<{ url: string }>("upload", {
    method: "POST",
    body: form,
  });
  return data.url;
}

export const cmsApi = {
  health: () => cmsRequest<{ status: string; time?: string }>("health"),
  dashboard: () =>
    cmsRequest<{
      stats: import("../types").CmsDashboardStats;
      activity: import("../types").CmsActivityItem[];
    }>("dashboard"),
  workspace: () => cmsRequest<import("../types").CmsWorkspaceOverview>("workspace"),
  getHomepage: () => cmsRequest<{ homepage: import("../types").CmsHomepage }>("homepage"),
  updateHomepage: (homepage: Partial<import("../types").CmsHomepage>) =>
    cmsRequest<{ homepage: import("../types").CmsHomepage }>("homepage", {
      method: "PUT",
      body: JSON.stringify(homepage),
    }),
  getPortfolio: () => cmsRequest<{ portfolio: import("../types").CmsPortfolioProject[] }>("portfolio"),
  getProject: (id: string) =>
    cmsRequest<{ project: import("../types").CmsPortfolioProject }>(`portfolio/${id}`),
  createProject: (project: import("../types").CmsPortfolioInput) =>
    cmsRequest<{ project: import("../types").CmsPortfolioProject }>("portfolio", {
      method: "POST",
      body: JSON.stringify(project),
    }),
  updateProject: (id: string, project: Partial<import("../types").CmsPortfolioInput>) =>
    cmsRequest<{ project: import("../types").CmsPortfolioProject }>(`portfolio/${id}`, {
      method: "PUT",
      body: JSON.stringify(project),
    }),
  deleteProject: (id: string) => cmsRequest<Record<string, never>>(`portfolio/${id}`, { method: "DELETE" }),
  reorderPortfolio: (ids: string[]) =>
    cmsRequest<{ portfolio: import("../types").CmsPortfolioProject[] }>("portfolio/reorder", {
      method: "PUT",
      body: JSON.stringify({ ids }),
    }),
  getFounder: () => cmsRequest<{ founder: import("../types").CmsFounder }>("founder"),
  updateFounder: (founder: Partial<import("../types").CmsFounder>) =>
    cmsRequest<{ founder: import("../types").CmsFounder }>("founder", {
      method: "PUT",
      body: JSON.stringify(founder),
    }),
  getCompany: () => cmsRequest<{ company: import("../types").CmsCompanyInfo }>("company"),
  updateCompany: (company: Partial<import("../types").CmsCompanyInfo>) =>
    cmsRequest<{ company: import("../types").CmsCompanyInfo }>("company", {
      method: "PUT",
      body: JSON.stringify(company),
    }),
  getSocial: () => cmsRequest<{ social: import("../types").CmsSocialLink[] }>("social"),
  saveSocial: (links: import("../types").CmsSocialLink[]) =>
    cmsRequest<{ social: import("../types").CmsSocialLink[] }>("social", {
      method: "PUT",
      body: JSON.stringify({ links }),
    }),
  getAnalytics: (range = "30d") =>
    cmsRequest<{ analytics: import("../types").CmsVisitorAnalytics }>("analytics", {
      query: { range },
    }),
  search: (q: string) =>
    cmsRequest<{ results: Array<{ module: string; label: string; to: string }> }>("search", {
      query: { q },
    }),
  publish: () => cmsRequest<{ publishedAt: string }>("publish", { method: "POST" }),
  unpublish: () => cmsRequest<{ unpublishedAt: string }>("publish", { method: "DELETE" }),
  uploadFile: cmsUploadFile,
};

/** @deprecated Token auth removed — session cookies are the source of truth. */
export function getCmsApiToken(): string {
  return "";
}

/** @deprecated No-op — session cookies are the source of truth. */
export function setCmsApiToken(_token: string | null): void {
  // intentionally empty
}

/** @deprecated Compatibility shim for legacy imports. */
export const CMS_API_TOKEN = "" as string;
